import type { DirectChannel, DirectMessageCandidate } from '../../types/index.js';
import type { RequestFn } from '../http.js';

/** Direct messages and group DMs (creation, rename, icon, participants,
 *  roles, permissions). */
export function makeDmsApi(request: RequestFn) {
  return {
    listDirectMessages: () => request<{ dm: DirectChannel[] }>('/api/dms'),
    /** Open (or create) the personal "Сейф" — the self-chat (Telegram Saved
     *  Messages). Idempotent; the only sanctioned self-DM. The vault is the DM
     *  whose `directParticipants` has just the owner. */
    openSavedVault: () => request<DirectChannel>('/api/me/vault', { method: 'POST' }),
    openDirectMessage: (userId: string) =>
      request<DirectChannel>('/api/dms', {
        method: 'POST',
        body: JSON.stringify({ userId }),
      }),
    createGroupDirectMessage: (input: { userIds: string[]; name?: string }) =>
      request<DirectChannel>('/api/dms/groups', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    renameGroupDirectMessage: (channelId: string, name: string | null) =>
      request<DirectChannel>(`/api/dms/${channelId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),
    /** Toggle message comment-threads for a group DM (OWNER/ADMIN — same PATCH). */
    setGroupDirectMessageComments: (channelId: string, commentsEnabled: boolean) =>
      request<DirectChannel>(`/api/dms/${channelId}`, {
        method: 'PATCH',
        body: JSON.stringify({ commentsEnabled }),
      }),
    addGroupDirectMessageParticipants: (channelId: string, userIds: string[]) =>
      request<DirectChannel>(`/api/dms/${channelId}/participants`, {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      }),
    listGroupDirectMessageCandidates: (channelId: string) =>
      request<{ users: DirectMessageCandidate[] }>(`/api/dms/${channelId}/candidates`),
    leaveGroupDirectMessage: (channelId: string) =>
      request<void>(`/api/dms/${channelId}/leave`, { method: 'POST' }),
    /** Clear all messages in a DM for EVERYONE (Telegram "clear history").
     *  1:1/vault: any participant; group DM: owner only (server-enforced). */
    clearDirectMessageHistory: (channelId: string) =>
      request<void>(`/api/dms/${channelId}/clear`, { method: 'POST' }),
    /** Delete a 1:1 chat "for me" — hide it from my list only (history and the
     *  other person's view are untouched). It re-surfaces if they write again. */
    hideDirectMessage: (channelId: string) =>
      request<void>(`/api/dms/${channelId}/hide`, { method: 'POST' }),
    /** OWNER or ADMIN sets the group avatar. Pass null to clear. */
    setGroupDirectMessageIcon: (channelId: string, iconUrl: string | null) =>
      request<DirectChannel>(`/api/dms/${channelId}/icon`, {
        method: 'PATCH',
        body: JSON.stringify({ iconUrl }),
      }),
    /** OWNER or ADMIN sets the channel-wide MEMBER permission bitmask. */
    setGroupDirectMessagePermissions: (channelId: string, memberPermissions: number) =>
      request<DirectChannel>(`/api/dms/${channelId}/permissions`, {
        method: 'PATCH',
        body: JSON.stringify({ memberPermissions }),
      }),
    /** OWNER promotes a MEMBER to ADMIN or demotes an ADMIN back. */
    setGroupDirectMessageParticipantRole: (
      channelId: string,
      userId: string,
      role: 'ADMIN' | 'MEMBER',
    ) =>
      request<DirectChannel>(`/api/dms/${channelId}/participants/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    /** OWNER or ADMIN kicks a participant. ADMIN can't kick OWNER or other ADMINs. */
    kickGroupDirectMessageParticipant: (channelId: string, userId: string) =>
      request<void>(`/api/dms/${channelId}/participants/${userId}`, {
        method: 'DELETE',
      }),
  };
}
